import { useCallback, useEffect, useRef, useState } from "react";
import type {
  AuthenticatedSession,
  CreateJobInput,
  PublicJob,
  OpenAIVideoModel,
  StudioSettings,
} from "@farzin/contracts";
import { ApiClient } from "../api/client";

export interface JobsState {
  readonly jobs: readonly PublicJob[];
  readonly session: AuthenticatedSession | null;
  readonly settings: StudioSettings | null;
  readonly loading: boolean;
  readonly submitting: boolean;
  readonly error: string | null;
  readonly create: (input: CreateJobInput) => Promise<void>;
  readonly updateVideoModel: (model: OpenAIVideoModel) => Promise<void>;
  readonly savingSettings: boolean;
  readonly refresh: () => Promise<void>;
}

export function useJobs(initData: string): JobsState {
  const clientRef = useRef(new ApiClient(initData));
  const [jobs, setJobs] = useState<readonly PublicJob[]>([]);
  const [session, setSession] = useState<AuthenticatedSession | null>(null);
  const [settings, setSettings] = useState<StudioSettings | null>(null);
  const [loading, setLoading] = useState(Boolean(initData));
  const [submitting, setSubmitting] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!initData) return;
    try {
      const [nextSession, nextJobs, nextSettings] = await Promise.all([
        clientRef.current.getSession(),
        clientRef.current.listJobs(),
        clientRef.current.getSettings(),
      ]);
      setSession(nextSession);
      setJobs(nextJobs);
      setSettings((current) => newestSettings(current, nextSettings));
      setError(null);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Could not load your videos.",
      );
    } finally {
      setLoading(false);
    }
  }, [initData]);

  const create = useCallback(async (input: CreateJobInput) => {
    setSubmitting(true);
    setError(null);
    try {
      const job = await clientRef.current.createJob(input);
      setJobs((current) => [job, ...current]);
      window.Telegram?.WebApp.HapticFeedback?.notificationOccurred("success");
    } catch (cause) {
      window.Telegram?.WebApp.HapticFeedback?.notificationOccurred("error");
      setError(
        cause instanceof Error ? cause.message : "Could not create your video.",
      );
      throw cause;
    } finally {
      setSubmitting(false);
    }
  }, []);

  const updateVideoModel = useCallback(
    async (model: OpenAIVideoModel): Promise<void> => {
      setSavingSettings(true);
      setError(null);
      try {
        const next = await clientRef.current.updateVideoModel(model);
        setSettings((current) => newestSettings(current, next));
        window.Telegram?.WebApp.HapticFeedback?.notificationOccurred("success");
      } catch (cause) {
        window.Telegram?.WebApp.HapticFeedback?.notificationOccurred("error");
        setError(
          cause instanceof Error
            ? cause.message
            : "Could not update the video model.",
        );
      } finally {
        setSavingSettings(false);
      }
    },
    [],
  );

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(() => void refresh(), 8_000);
    return () => window.clearInterval(interval);
  }, [refresh]);

  return {
    jobs,
    session,
    settings,
    loading,
    submitting,
    savingSettings,
    error,
    create,
    updateVideoModel,
    refresh,
  };
}

function newestSettings(
  current: StudioSettings | null,
  incoming: StudioSettings,
): StudioSettings {
  if (!current?.updatedAt) return incoming;
  if (!incoming.updatedAt) return current;
  return incoming.updatedAt >= current.updatedAt ? incoming : current;
}
